/**
 * Admin Panel
 * Verwaltungs-Dashboard für Administratoren
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Admin.module.css';
import { authApi, adminApi, statsApi, libraryApi } from '../api';
import { useConfirm } from '../context/ConfirmContext';

function Admin() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('overview');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilters, setLogFilters] = useState({
    level: 'INFO', from: '', to: '', pageSize: 100
  });

  // User-Management State
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwEditError, setPwEditError] = useState(null);
  const [deleteBandId, setDeleteBandId] = useState('');
  const [sysStats, setSysStats] = useState(null);

  // Shop-/Affiliate-Verwaltung State
  const [shopProviders, setShopProviders] = useState([]);
  const [shopProvidersLoading, setShopProvidersLoading] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null); // null = neu anlegen, sonst provider-Objekt
  const emptyProviderForm = { name: '', url_template: '', affiliate_param_key: '', affiliate_param_value: '', logo_media_key: '', active: true, sort_order: 0 };
  const [providerForm, setProviderForm] = useState(emptyProviderForm);
  const [providerFormOpen, setProviderFormOpen] = useState(false);

  // Job-Runs State
  const [jobRuns, setJobRuns] = useState([]);
  const [jobRunsLoading, setJobRunsLoading] = useState(false);
  const [jobNameFilter, setJobNameFilter] = useState('');

  // Legacy-User-Audit State
  const [legacyAuditResult, setLegacyAuditResult] = useState(null);
  const [legacyAuditLoading, setLegacyAuditLoading] = useState(false);

  // Audit-Log State (§59)
  const [auditLog, setAuditLog] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditLogCursor, setAuditLogCursor] = useState(null);
  const [auditLogLoadedOnce, setAuditLogLoadedOnce] = useState(false);

  // Katalogverwaltung State (§36)
  const [catalogResource, setCatalogResource] = useState('genres'); // genres|authors|publishers
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [newCatalogName, setNewCatalogName] = useState('');
  const [catalogEditId, setCatalogEditId] = useState(null);
  const [catalogEditName, setCatalogEditName] = useState('');
  const [catalogMergeTarget, setCatalogMergeTarget] = useState({}); // id -> Ziel-ID (string)

  // API-Nutzungsstatistik State
  const [apiUsage, setApiUsage] = useState([]);
  const [apiUsageLoading, setApiUsageLoading] = useState(false);
  const [apiUsageUserFilter, setApiUsageUserFilter] = useState('');

  // Pro-User Rate-Limit-Overrides State (im User-Edit-Panel)
  const [userRateLimits, setUserRateLimits] = useState([]);
  const [rateLimitsLoading, setRateLimitsLoading] = useState(false);
  const [newRateLimit, setNewRateLimit] = useState({ limit_key: 'isbn-lookup', max_attempts: '', window_seconds: '' });

  // Manga-/Band-Verwaltung State (§48/§49)
  const emptyMangaForm = { title: '', description: '', status: 'RUNNING', publisher: '', genres: '', cover_media_key: '' };
  const [newMangaForm, setNewMangaForm] = useState(emptyMangaForm);
  const [editMangaId, setEditMangaId] = useState('');
  const [editMangaForm, setEditMangaForm] = useState(emptyMangaForm);
  const [editMangaLoaded, setEditMangaLoaded] = useState(false);

  const emptyBandForm = { band_number: '', band_title: '', author: '', publisher: '', published_date: '', language: 'de', cover_media_key: '' };
  const [newBandMangaId, setNewBandMangaId] = useState('');
  const [newBandForm, setNewBandForm] = useState(emptyBandForm);
  const [editBandId, setEditBandId] = useState('');
  const [editBandForm, setEditBandForm] = useState(emptyBandForm);

  // Prüfe Admin-Berechtigung
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const result = await authApi.getMe();

        if (result.success && result.data) {
          // Prüfe role (0=Admin, 1=Mod)
          const role = result.data.role;
          const hasAdminRole = adminApi.isModerator(role);
          setIsAdmin(hasAdminRole);
          if (hasAdminRole) {
            // System-Stats laden (GET /admin/stats liefert user_count + mehr)
            adminApi.getAdminStats().then(r => {
              if (r.success) setSysStats(r.data);
              else statsApi.getOverview().then(r2 => { if (r2.success) setSysStats(r2.data); });
            }).catch(() => statsApi.getOverview().then(r => { if (r.success) setSysStats(r.data); }));
          }
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Admin check failed:', error);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkAdmin();
  }, []);

  // Admin-Aktion ausführen
  const executeAdminAction = async (actionKey, actionName) => {
    setActionLoading(actionName);
    setMessage(null);

    try {
      let result;
      switch (actionKey) {
        case 'update_all_mangas':
          result = await adminApi.updateAllMangas();
          break;
        case 'download_missing_covers':
          result = await adminApi.downloadMissingCovers();
          break;
        case 'reindex_band_counts':
          result = await adminApi.reindexBandCounts();
          break;
        case 'rescan_all_isbn':
          result = await adminApi.rescanAllIsbn();
          break;
        default:
          throw new Error(t('admin.unknownAction'));
      }

      if (result.success) {
        setMessage({ type: 'success', text: t('admin.actionSuccess', { name: actionName }) });
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.executionError') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('admin.errorWithMessage', { message: error.message }) });
    } finally {
      setActionLoading(null);
    }
  };

  // Logs laden
  const loadLogs = async () => {
    setLogsLoading(true);
    setMessage(null);
    try {
      const result = await adminApi.getLogs(logFilters);
      if (result.success) {
        setLogs(result.data || []);
        if ((result.data || []).length === 0) {
          setMessage({ type: 'info', text: t('admin.noLogsForFilter') });
        }
      } else {
        const code = result.error?.code;
        if (code === 'NOT_FOUND' || result.error?.status === 404) {
          setMessage({ type: 'warning', text: t('admin.logsEndpointMissing') });
        } else {
          setMessage({ type: 'error', text: result.error?.message || t('admin.errorLoadingLogs') });
        }
        setLogs([]);
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('admin.errorWithMessage', { message: error.message }) });
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  // User-Liste laden
  const loadUsers = async (q = userSearch) => {
    setUsersLoading(true);
    try {
      const result = await adminApi.getUsers(q);
      if (result.success) {
        setUsers(result.data?.items || result.data || []);
      } else {
        const code = result.error?.code;
        if (code === 'NOT_FOUND' || result.error?.status === 404) {
          setMessage({ type: 'warning', text: t('admin.userMgmtEndpointMissing') });
        } else {
          setMessage({ type: 'error', text: result.error?.message || t('admin.errorLoadingUsers') });
        }
        setUsers([]);
      }
    } finally {
      setUsersLoading(false);
    }
  };

  // Rolle ändern
  const handleRoleChange = async () => {
    if (!selectedUser || newRole === '') return;
    setActionLoading('role_' + selectedUser.id);
    try {
      const result = await adminApi.updateUserRole(selectedUser.id, parseInt(newRole));
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.roleChanged', { username: selectedUser.username, role: adminApi.ROLE_LABELS[newRole] }) });
        setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, role: parseInt(newRole) } : u));
        setSelectedUser(null); setNewRole('');
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorChangingRole') });
      }
    } catch (e) { setMessage({ type: 'error', text: e.message }); }
    finally { setActionLoading(null); }
  };

  // Passwort zurücksetzen
  const handleResetPw = async () => {
    if (!selectedUser || !newPw.trim()) return;
    setPwEditError(null);

    // Client-seitige Validierung (gleiche Regeln wie Backend)
    if (newPw.length < 8) {
      setPwEditError(t('admin.pwMinLength'));
      return;
    }
    if (!/[A-Z]/.test(newPw)) {
      setPwEditError(t('admin.pwNeedsUpper'));
      return;
    }
    if (!/[a-z]/.test(newPw)) {
      setPwEditError(t('admin.pwNeedsLower'));
      return;
    }
    if (!/[0-9]/.test(newPw)) {
      setPwEditError(t('admin.pwNeedsNumber'));
      return;
    }
    if (!/[^A-Za-z0-9]/.test(newPw)) {
      setPwEditError(t('admin.pwNeedsSpecial'));
      return;
    }

    setActionLoading('pw_' + selectedUser.id);
    try {
      const result = await adminApi.resetUserPassword(selectedUser.id, newPw);
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.pwReset', { username: selectedUser.username }) });
        setSelectedUser(null); setNewPw(''); setPwEditError(null);
      } else {
        const code = result.error?.code;
        if (code === 'VALIDATION_ERROR') {
          setPwEditError(t('admin.pwRequirements'));
        } else if (code === 'NOT_FOUND' || result.error?.status === 404) {
          setPwEditError(t('admin.pwEndpointMissing'));
        } else {
          setPwEditError(result.error?.message || t('admin.unknownError'));
        }
      }
    } catch (e) { setPwEditError(e.message); }
    finally { setActionLoading(null); }
  };

  // User löschen
  const handleDeleteUser = async (user) => {
    if (!(await confirm(t('admin.confirmDeleteUser', { username: user.username }), { danger: true }))) return;
    setActionLoading('del_user_' + user.id);
    try {
      const result = await adminApi.deleteUser(user.id);
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.userDeleted', { username: user.username }) });
        setUsers(prev => prev.filter(u => u.id !== user.id));
        if (selectedUser?.id === user.id) setSelectedUser(null);
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorGeneric') });
      }
    } catch (e) { setMessage({ type: 'error', text: e.message }); }
    finally { setActionLoading(null); }
  };

  // Band löschen
  const handleDeleteBand = async () => {
    const id = parseInt(deleteBandId, 10);
    if (!id || isNaN(id)) { setMessage({ type: 'error', text: t('admin.enterValidBandId') }); return; }
    if (!(await confirm(t('admin.confirmDeleteBandGlobal', { id }), { danger: true }))) return;
    setActionLoading('delete_band');
    try {
      const result = await adminApi.deleteBand(id);
      if (result.success) { setMessage({ type: 'success', text: t('admin.bandDeleted', { id }) }); setDeleteBandId(''); }
      else setMessage({ type: 'error', text: result.error?.message || t('admin.errorGeneric') });
    } catch (e) { setMessage({ type: 'error', text: e.message }); }
    finally { setActionLoading(null); }
  };

  // ── Shop-/Affiliate-Verwaltung ──────────────────────────────

  const loadShopProviders = async () => {
    setShopProvidersLoading(true);
    try {
      const result = await adminApi.getShopProviders();
      if (result.success) {
        setShopProviders(result.data || []);
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorLoadingProviders') });
      }
    } finally {
      setShopProvidersLoading(false);
    }
  };

  const openNewProviderForm = () => {
    setEditingProvider(null);
    setProviderForm(emptyProviderForm);
    setProviderFormOpen(true);
  };

  const openEditProviderForm = (provider) => {
    setEditingProvider(provider);
    setProviderForm({
      name: provider.name || '',
      url_template: provider.url_template || '',
      affiliate_param_key: provider.affiliate_param_key || '',
      affiliate_param_value: provider.affiliate_param_value || '',
      logo_media_key: provider.logo_media_key || '',
      active: provider.active ?? true,
      sort_order: provider.sort_order ?? 0,
    });
    setProviderFormOpen(true);
  };

  const handleSaveProvider = async () => {
    if (!providerForm.name.trim() || !providerForm.url_template.trim()) {
      setMessage({ type: 'error', text: t('admin.providerNameUrlRequired') });
      return;
    }
    const payload = {
      ...providerForm,
      affiliate_param_key: providerForm.affiliate_param_key || null,
      affiliate_param_value: providerForm.affiliate_param_value || null,
      logo_media_key: providerForm.logo_media_key || null,
      sort_order: parseInt(providerForm.sort_order, 10) || 0,
    };
    setActionLoading('save_provider');
    try {
      const result = editingProvider
        ? await adminApi.updateShopProvider(editingProvider.id, payload)
        : await adminApi.createShopProvider(payload);
      if (result.success) {
        setMessage({ type: 'success', text: editingProvider ? t('admin.providerUpdated') : t('admin.providerCreated') });
        setProviderFormOpen(false);
        setEditingProvider(null);
        setProviderForm(emptyProviderForm);
        loadShopProviders();
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorSaving') });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteProvider = async (provider) => {
    if (!(await confirm(t('admin.confirmDeleteProvider', { name: provider.name }), { danger: true }))) return;
    setActionLoading('del_provider_' + provider.id);
    try {
      const result = await adminApi.deleteShopProvider(provider.id);
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.providerDeleted', { name: provider.name }) });
        setShopProviders(prev => prev.filter(p => p.id !== provider.id));
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorDeleting') });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setActionLoading(null);
    }
  };

  // ── Job-Runs Monitoring ──────────────────────────────────────

  const loadJobRuns = async () => {
    setJobRunsLoading(true);
    try {
      const result = await adminApi.getJobRuns({ job_name: jobNameFilter || undefined, limit: 50 });
      if (result.success) {
        setJobRuns(result.data || []);
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorLoadingJobRuns') });
        setJobRuns([]);
      }
    } finally {
      setJobRunsLoading(false);
    }
  };

  // ── Legacy-User-Audit ────────────────────────────────────────

  const runLegacyAudit = async (dryRun) => {
    if (!dryRun && !(await confirm(t('admin.confirmLegacyCleanup'), { danger: true }))) {
      return;
    }
    setLegacyAuditLoading(true);
    setMessage(null);
    try {
      const result = await adminApi.auditLegacyUsers(dryRun);
      if (result.success) {
        setLegacyAuditResult(result.data);
        setMessage({ type: 'success', text: dryRun ? t('admin.auditDryRunDone') : t('admin.cleanupDone') });
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorAudit') });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setLegacyAuditLoading(false);
    }
  };

  // ── Audit-Log (§59) ──────────────────────────────────────────

  const loadAuditLog = async (cursor = null) => {
    setAuditLogLoading(true);
    setAuditLogLoadedOnce(true);
    try {
      const result = await adminApi.getAuditLog({ cursor, limit: 100 });
      if (result.success) {
        const items = result.data?.items || [];
        setAuditLog(prev => (cursor ? [...prev, ...items] : items));
        setAuditLogCursor(result.data?.next_cursor ?? null);
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorLoadingAuditLog') });
      }
    } finally {
      setAuditLogLoading(false);
    }
  };

  // ── Katalogverwaltung: Genres/Autoren/Verlage (§36) ───────────

  const CATALOG_LABEL_KEYS = { genres: 'admin.catalogGenres', authors: 'admin.catalogAuthors', publishers: 'admin.catalogPublishers' };

  const loadCatalog = async (resource = catalogResource) => {
    setCatalogLoading(true);
    try {
      const result = await adminApi.getCatalog(resource);
      if (result.success) {
        setCatalogItems(result.data?.items || []);
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorLoadingGeneric') });
        setCatalogItems([]);
      }
    } finally {
      setCatalogLoading(false);
    }
  };

  const handleCreateCatalogEntry = async () => {
    if (!newCatalogName.trim()) return;
    setActionLoading('new_catalog');
    try {
      const result = await adminApi.createCatalogEntry(catalogResource, newCatalogName.trim());
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.catalogEntryCreated', { name: newCatalogName }) });
        setNewCatalogName('');
        loadCatalog();
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorCreating') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleRenameCatalogEntry = async (id) => {
    if (!catalogEditName.trim()) return;
    setActionLoading('rename_catalog_' + id);
    try {
      const result = await adminApi.renameCatalogEntry(catalogResource, id, catalogEditName.trim());
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.catalogRenamed') });
        setCatalogEditId(null);
        loadCatalog();
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorRenaming') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleMergeCatalogEntry = async (id) => {
    const targetId = parseInt(catalogMergeTarget[id], 10);
    if (!targetId) { setMessage({ type: 'error', text: t('admin.enterMergeTargetId') }); return; }
    if (!(await confirm(t('admin.confirmMergeEntry', { id, targetId }), { danger: true }))) return;
    setActionLoading('merge_catalog_' + id);
    try {
      const result = await adminApi.mergeCatalogEntry(catalogResource, id, targetId);
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.catalogMerged') });
        setCatalogMergeTarget(prev => ({ ...prev, [id]: '' }));
        loadCatalog();
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorMerging') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteCatalogEntry = async (id) => {
    if (!(await confirm(t('admin.confirmDeleteEntry', { id }), { danger: true }))) return;
    setActionLoading('del_catalog_' + id);
    try {
      const result = await adminApi.deleteCatalogEntry(catalogResource, id);
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.deletedGeneric') });
        setCatalogItems(prev => prev.filter(c => c.id !== id));
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorDeleting') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  // ── API-Nutzungsstatistik ──────────────────────────────────────

  const loadApiUsage = async () => {
    setApiUsageLoading(true);
    try {
      const result = await adminApi.getApiUsage({
        user_id: apiUsageUserFilter ? parseInt(apiUsageUserFilter, 10) : undefined,
        limit: 100,
      });
      if (result.success) {
        setApiUsage(result.data || []);
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorLoadingApiUsage') });
        setApiUsage([]);
      }
    } finally {
      setApiUsageLoading(false);
    }
  };

  // ── Pro-User Rate-Limit-Overrides (im User-Edit-Panel) ─────────

  const loadUserRateLimits = async (userId) => {
    setRateLimitsLoading(true);
    try {
      const result = await adminApi.getUserRateLimits(userId);
      setUserRateLimits(result.success ? (result.data || []) : []);
    } finally {
      setRateLimitsLoading(false);
    }
  };

  const handleSetRateLimit = async () => {
    if (!selectedUser) return;
    const maxAttempts = parseInt(newRateLimit.max_attempts, 10);
    const windowSeconds = parseInt(newRateLimit.window_seconds, 10);
    if (!newRateLimit.limit_key.trim() || !maxAttempts || !windowSeconds) {
      setMessage({ type: 'error', text: t('admin.fillRateLimitFields') });
      return;
    }
    setActionLoading('set_ratelimit');
    try {
      const result = await adminApi.setUserRateLimit(selectedUser.id, newRateLimit.limit_key.trim(), {
        max_attempts: maxAttempts, window_seconds: windowSeconds,
      });
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.rateLimitSet') });
        loadUserRateLimits(selectedUser.id);
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorSettingRateLimit') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteRateLimit = async (limitKey) => {
    if (!selectedUser) return;
    setActionLoading('del_ratelimit_' + limitKey);
    try {
      const result = await adminApi.deleteUserRateLimit(selectedUser.id, limitKey);
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.rateLimitRemoved') });
        loadUserRateLimits(selectedUser.id);
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorRemoving') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  // ── Manga-/Band-Verwaltung (§48/§49) ────────────────────────────

  const handleCreateManga = async () => {
    if (!newMangaForm.title.trim()) { setMessage({ type: 'error', text: t('admin.titleRequired') }); return; }
    setActionLoading('create_manga');
    try {
      const payload = {
        ...newMangaForm,
        genres: newMangaForm.genres ? newMangaForm.genres.split(',').map(g => g.trim()).filter(Boolean) : [],
        cover_media_key: newMangaForm.cover_media_key || null,
      };
      const result = await adminApi.createManga(payload);
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.seriesCreated', { title: newMangaForm.title, id: result.data?.id }) });
        setNewMangaForm(emptyMangaForm);
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorCreating') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleLoadMangaForEdit = async () => {
    const id = parseInt(editMangaId, 10);
    if (!id) { setMessage({ type: 'error', text: t('admin.enterValidMangaId') }); return; }
    setActionLoading('load_manga');
    try {
      const result = await libraryApi.getMangaById(id);
      if (result.success && result.data) {
        const m = result.data;
        setEditMangaForm({
          title: m.title || '', description: m.description || '', status: m.status || 'RUNNING',
          publisher: m.publisher || '', genres: (m.genres || []).join(', '), cover_media_key: m.cover_media_key || '',
        });
        setEditMangaLoaded(true);
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.mangaNotFound') });
        setEditMangaLoaded(false);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateManga = async () => {
    const id = parseInt(editMangaId, 10);
    if (!id) return;
    setActionLoading('update_manga');
    try {
      const payload = {
        ...editMangaForm,
        genres: editMangaForm.genres ? editMangaForm.genres.split(',').map(g => g.trim()).filter(Boolean) : [],
        cover_media_key: editMangaForm.cover_media_key || null,
      };
      const result = await adminApi.updateManga(id, payload);
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.seriesUpdated', { id }) });
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorSaving') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetMangaActive = async (active) => {
    const id = parseInt(editMangaId, 10);
    if (!id) { setMessage({ type: 'error', text: t('admin.enterValidMangaId') }); return; }
    if (!active && !(await confirm(t('admin.confirmDeactivateSeries', { id }), { danger: true }))) return;
    setActionLoading('toggle_manga_active');
    try {
      const result = await adminApi.setMangaActive(id, active);
      if (result.success) {
        setMessage({ type: 'success', text: t(active ? 'admin.seriesActivated' : 'admin.seriesDeactivated', { id }) });
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorGeneric') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateBand = async () => {
    const mangaId = parseInt(newBandMangaId, 10);
    const bandNumber = parseInt(newBandForm.band_number, 10);
    if (!mangaId || !bandNumber) { setMessage({ type: 'error', text: t('admin.mangaIdAndNumberRequired') }); return; }
    setActionLoading('create_band');
    try {
      const payload = { ...newBandForm, band_number: bandNumber, cover_media_key: newBandForm.cover_media_key || null };
      const result = await adminApi.createBand(mangaId, payload);
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.bandCreated', { id: result.data?.id }) });
        setNewBandForm(emptyBandForm);
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorCreating') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateBand = async () => {
    const id = parseInt(editBandId, 10);
    const bandNumber = parseInt(editBandForm.band_number, 10);
    if (!id) { setMessage({ type: 'error', text: t('admin.enterValidBandId') }); return; }
    if (!bandNumber) { setMessage({ type: 'error', text: t('admin.bandNumberRequired') }); return; }
    setActionLoading('update_band');
    try {
      const payload = { ...editBandForm, band_number: bandNumber, cover_media_key: editBandForm.cover_media_key || null };
      const result = await adminApi.updateBand(id, payload);
      if (result.success) {
        setMessage({ type: 'success', text: t('admin.bandUpdated', { id }) });
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorSaving') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetBandActive = async (active) => {
    const id = parseInt(editBandId, 10);
    if (!id) { setMessage({ type: 'error', text: t('admin.enterValidBandId') }); return; }
    if (!active && !(await confirm(t('admin.confirmDeactivateBand', { id }), { danger: true }))) return;
    setActionLoading('toggle_band_active');
    try {
      const result = await adminApi.setBandActive(id, active);
      if (result.success) {
        setMessage({ type: 'success', text: t(active ? 'admin.bandActivated' : 'admin.bandDeactivated', { id }) });
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('admin.errorGeneric') });
      }
    } finally {
      setActionLoading(null);
    }
  };

  // API-Nutzung: Aufrufe pro Endpoint aggregiert (Top 10) fürs Balkendiagramm
  const apiUsageChartData = useMemo(() => {
    const byEndpoint = {};
    apiUsage.forEach(row => {
      const key = row.endpoint || '—';
      byEndpoint[key] = (byEndpoint[key] || 0) + (row.request_count || 0);
    });
    const entries = Object.entries(byEndpoint)
      .map(([endpoint, count]) => ({ endpoint, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    const max = entries.reduce((m, e) => Math.max(m, e.count), 0);
    return { entries, max };
  }, [apiUsage]);

  // Median der Aufrufe pro Zeile (User+Endpoint-Kombination) - robuster gegen
  // Ausreißer (z.B. ein Skript, das einen einzelnen Endpoint massenhaft aufruft)
  const apiUsageMedian = useMemo(() => {
    const counts = apiUsage.map(r => r.request_count || 0).sort((a, b) => a - b);
    if (counts.length === 0) return 0;
    const mid = Math.floor(counts.length / 2);
    return counts.length % 2 !== 0 ? counts[mid] : (counts[mid - 1] + counts[mid]) / 2;
  }, [apiUsage]);

  // API-Nutzung pro User aggregiert: mehrere Zeilen (User+Endpoint) je User werden
  // zusammengefasst. activeDays = Spanne zwischen erster und letzter beobachteter
  // Anfrage dieses Users (min. 1 Tag) - dadurch werden ältere/inaktive User (kleine
  // Rate über lange Zeit) nicht mit neuen, aktiven Usern (hohe Rate über kurze Zeit)
  // vermischt: wir vergleichen Aufrufe/Tag statt der reinen Gesamtsumme.
  const apiUsageByUser = useMemo(() => {
    const byUser = {};
    apiUsage.forEach(row => {
      const uid = row.user_id;
      if (uid == null) return;
      if (!byUser[uid]) {
        byUser[uid] = { userId: uid, totalCalls: 0, endpointCount: 0, earliestAt: null, latestAt: null };
      }
      const u = byUser[uid];
      u.totalCalls += row.request_count || 0;
      u.endpointCount += 1;
      const first = row.first_request_at ? new Date(row.first_request_at).getTime() : null;
      const last = row.last_request_at ? new Date(row.last_request_at).getTime() : null;
      if (first != null && (u.earliestAt == null || first < u.earliestAt)) u.earliestAt = first;
      if (last != null && (u.latestAt == null || last > u.latestAt)) u.latestAt = last;
    });
    return Object.values(byUser).map(u => {
      const spanMs = u.earliestAt != null && u.latestAt != null ? Math.max(u.latestAt - u.earliestAt, 0) : 0;
      const activeDays = Math.max(spanMs / 86400000, 1); // min. 1 Tag gg. Division durch 0
      return { ...u, activeDays, callsPerDay: u.totalCalls / activeDays };
    });
  }, [apiUsage]);

  // Ø Aufrufe/Tag über alle User im geladenen Zeitraum - Zeitraum-bereinigter
  // Durchschnitt statt "Gesamtaufrufe / Useranzahl", da sonst wenige, schon lange
  // aktive User den Schnitt für neue/inaktive User verfälschen würden.
  const apiUsagePeriodAvg = useMemo(() => {
    if (apiUsageByUser.length === 0) return 0;
    const sum = apiUsageByUser.reduce((s, u) => s + u.callsPerDay, 0);
    return sum / apiUsageByUser.length;
  }, [apiUsageByUser]);

  // Auffällige User automatisch erkennen: Ausreißer bei Aufrufen/Tag (Std-Abweichung
  // vom Mittelwert) - robuster gegen einzelne Extremwerte zusätzlich mit dem
  // 3-fachen Median abgesichert. Mind. 5 Aufrufe gefordert, damit ein einzelner
  // "Zufalls"-Call keinen User fälschlich markiert.
  const apiUsageOutliers = useMemo(() => {
    if (apiUsageByUser.length < 2) return [];
    const rates = apiUsageByUser.map(u => u.callsPerDay).sort((a, b) => a - b);
    const mean = rates.reduce((s, r) => s + r, 0) / rates.length;
    const variance = rates.reduce((s, r) => s + (r - mean) ** 2, 0) / rates.length;
    const stdDev = Math.sqrt(variance);
    const mid = Math.floor(rates.length / 2);
    const medianRate = rates.length % 2 !== 0 ? rates[mid] : (rates[mid - 1] + rates[mid]) / 2;
    const threshold = Math.max(mean + 2 * stdDev, medianRate * 3, 5);
    return apiUsageByUser
      .filter(u => u.totalCalls >= 5 && u.callsPerDay > threshold)
      .sort((a, b) => b.callsPerDay - a.callsPerDay);
  }, [apiUsageByUser]);

  // Admin-Aktionen (endpoints müssen den switch-cases entsprechen)
  const adminActions = [
    { id: 'update_all_mangas',       titleKey: 'admin.actionUpdateAllTitle',      descKey: 'admin.actionUpdateAllDesc',      icon: '🔄', endpoint: 'update_all_mangas' },
    { id: 'download_missing_covers', titleKey: 'admin.actionDownloadCoversTitle', descKey: 'admin.actionDownloadCoversDesc', icon: '🖼️', endpoint: 'download_missing_covers' },
    { id: 'reindex_band_counts',     titleKey: 'admin.actionReindexTitle',        descKey: 'admin.actionReindexDesc',        icon: '📊', endpoint: 'reindex_band_counts' },
    { id: 'rescan_all_isbn',         titleKey: 'admin.actionRescanIsbnTitle',     descKey: 'admin.actionRescanIsbnDesc',     icon: '📚', endpoint: 'rescan_all_isbn' },
  ].map(a => ({ ...a, title: t(a.titleKey), description: t(a.descKey) }));

  if (isLoading) {
    return (
      <div className={styles.adminContainer}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>{t('admin.checkingPermissions')}</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className={styles.adminContainer}>
        <div className={styles.accessDenied}>
          <span className={styles.deniedIcon}>🚫</span>
          <h1>{t('admin.accessDenied')}</h1>
          <p>{t('admin.accessDeniedText')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminContainer}>
      {/* Header */}
      <div className={styles.adminHeader}>
        <div className={styles.headerContent}>
          <span className={styles.adminBadge}>👑 Admin</span>
          <h1>{t('admin.dashboardTitle')}</h1>
          <p>{t('admin.dashboardSubtitle')}</p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          <span>{message.type === 'success' ? '✅' : message.type === 'info' ? 'ℹ️' : message.type === 'warning' ? '⚠️' : '❌'}</span>
          <p>{message.text}</p>
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {/* Tab Navigation */}
      <nav className={styles.tabNav}>
        <button
          className={`${styles.tabButton} ${activeTab === 'overview' ? styles.active : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <span className={styles.tabIcon}>📊</span>
          {t('admin.tabOverview')}
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'users' ? styles.active : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <span className={styles.tabIcon}>👥</span>
          {t('admin.tabUsers')}
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'catalog' ? styles.active : ''}`}
          onClick={() => setActiveTab('catalog')}
        >
          <span className={styles.tabIcon}>📘</span>
          {t('admin.tabCatalog')}
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'shop' ? styles.active : ''}`}
          onClick={() => setActiveTab('shop')}
        >
          <span className={styles.tabIcon}>🛒</span>
          {t('admin.tabShop')}
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'monitoring' ? styles.active : ''}`}
          onClick={() => setActiveTab('monitoring')}
        >
          <span className={styles.tabIcon}>📡</span>
          {t('admin.tabMonitoring')}
        </button>
      </nav>

      <div className={styles.tabContent}>
      {/* Übersicht: Admin Actions */}
      {activeTab === 'overview' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>⚡</div>
          <div>
            <h2>{t('admin.backendActionsTitle')}</h2>
            <p>{t('admin.backendActionsDesc')}</p>
          </div>
        </div>

        <div className={styles.actionsGrid}>
          {adminActions.map(action => (
            <div key={action.id} className={styles.actionCard}>
              <div className={styles.actionIcon}>{action.icon}</div>
              <div className={styles.actionContent}>
                <h3>{action.title}</h3>
                <p>{action.description}</p>
              </div>
              <button
                className={styles.actionBtn}
                onClick={() => executeAdminAction(action.endpoint, action.title)}
                disabled={actionLoading === action.title}
              >
                {actionLoading === action.title ? (
                  <span className={styles.btnSpinner}></span>
                ) : (
                  t('admin.execute')
                )}
              </button>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Backend Logs */}
      {activeTab === 'overview' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>📋</div>
          <div>
            <h2>{t('admin.backendLogsTitle')}</h2>
            <p>{t('admin.backendLogsDesc')}</p>
          </div>
        </div>

        <div className={styles.logFilters}>
          <div className={styles.filterGroup}>
            <label>{t('admin.logLevel')}</label>
            <select
              value={logFilters.level}
              onChange={(e) => setLogFilters(prev => ({ ...prev, level: e.target.value }))}
            >
              <option value="DEBUG">DEBUG</option>
              <option value="INFO">INFO</option>
              <option value="WARN">WARN</option>
              <option value="ERROR">ERROR</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label>{t('admin.from')}</label>
            <input
              type="datetime-local"
              value={logFilters.from}
              onChange={(e) => setLogFilters(prev => ({ ...prev, from: e.target.value }))}
            />
          </div>

          <div className={styles.filterGroup}>
            <label>{t('admin.to')}</label>
            <input
              type="datetime-local"
              value={logFilters.to}
              onChange={(e) => setLogFilters(prev => ({ ...prev, to: e.target.value }))}
            />
          </div>

          <div className={styles.filterGroup}>
            <label>{t('admin.count')}</label>
            <select
              value={logFilters.pageSize}
              onChange={(e) => setLogFilters(prev => ({ ...prev, pageSize: parseInt(e.target.value) }))}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>

          <button
            className={styles.loadLogsBtn}
            onClick={loadLogs}
            disabled={logsLoading}
          >
            {logsLoading ? t('admin.loadingShort') : t('admin.loadLogs')}
          </button>
        </div>

        <div className={styles.logsContainer}>
          {logs.length === 0 ? (
            <div className={styles.noLogs}>
              <span>📭</span>
              <p>{t('admin.noLogsYet')}</p>
            </div>
          ) : (
            <div className={styles.logsList}>
              {logs.map((log, index) => (
                <div key={index} className={`${styles.logEntry} ${styles[`log${log.level}`]}`}>
                  <span className={styles.logTime}>{log.timestamp}</span>
                  <span className={styles.logLevel}>{log.level}</span>
                  <span className={styles.logMessage}>{log.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {/* System Stats */}
      {activeTab === 'overview' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>📈</div>
          <div><h2>{t('admin.systemStatsTitle')}</h2><p>{t('admin.systemStatsDesc')}</p></div>
        </div>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}><span className={styles.statIcon}>👤</span><div className={styles.statContent}><span className={styles.statValue}>{sysStats?.user_count ?? '--'}</span><span className={styles.statLabel}>{t('admin.statUsers')}</span></div></div>
          <div className={styles.statCard}><span className={styles.statIcon}>📚</span><div className={styles.statContent}><span className={styles.statValue}>{sysStats?.manga_count ?? '--'}</span><span className={styles.statLabel}>{t('admin.statMangas')}</span></div></div>
          <div className={styles.statCard}><span className={styles.statIcon}>📖</span><div className={styles.statContent}><span className={styles.statValue}>{sysStats?.band_count ?? '--'}</span><span className={styles.statLabel}>{t('admin.statBands')}</span></div></div>
          <div className={styles.statCard}><span className={styles.statIcon}>💾</span><div className={styles.statContent}><span className={styles.statValue}>{sysStats?.db_size_human ?? '--'}</span><span className={styles.statLabel}>{t('admin.statDbSize')}</span></div></div>
          <div className={styles.statCard}><span className={styles.statIcon}>⚠️</span><div className={styles.statContent}><span className={styles.statValue}>{sysStats?.failed_jobs_count ?? '--'}</span><span className={styles.statLabel}>{t('admin.statFailedJobs')}</span></div></div>
          <div className={styles.statCard}><span className={styles.statIcon}>📧</span><div className={styles.statContent}><span className={styles.statValue}>{sysStats?.failed_emails_count_24h ?? '--'}</span><span className={styles.statLabel}>{t('admin.statFailedEmails')}</span></div></div>
        </div>
      </section>
      )}

      {/* User Management */}
      {activeTab === 'users' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>👥</div>
          <div>
            <h2>{t('admin.userMgmtTitle')}</h2>
            <p>{t('admin.userMgmtDesc')}</p>
          </div>
        </div>

        <div className={styles.userSearchRow}>
          <input
            className={styles.userSearchInput}
            type="text"
            placeholder={t('admin.searchUsernameEmail')}
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadUsers()}
          />
          <button className={styles.actionBtn} onClick={() => loadUsers()} disabled={usersLoading}>
            {usersLoading ? <span className={styles.btnSpinner}></span> : `🔍 ${t('admin.search')}`}
          </button>
        </div>

        {users.length > 0 && (
          <div className={styles.userTable}>
            <div className={styles.userTableHeader}>
              <span>ID</span><span>{t('admin.username')}</span><span>{t('admin.email')}</span><span>{t('admin.role')}</span><span>{t('admin.actions')}</span>
            </div>
            {users.map(user => (
              <div key={user.id} className={`${styles.userRow} ${selectedUser?.id === user.id ? styles.userRowSelected : ''}`}>
                <span className={styles.userId}>#{user.id}</span>
                <span className={styles.userName2}>{user.username}</span>
                <span className={styles.userEmail}>{user.email}</span>
                <span className={`${styles.userRole} ${styles[`role${user.role}`]}`}>
                  {adminApi.ROLE_LABELS[user.role] ?? `Role ${user.role}`}
                </span>
                <div className={styles.userActions}>
                  <button
                    className={styles.userActionBtn}
                    title={t('admin.edit')}
                    onClick={() => {
                      setSelectedUser(user);
                      setNewRole(String(user.role));
                      setNewPw('');
                      setUserRateLimits([]);
                      loadUserRateLimits(user.id);
                    }}
                  >✏️</button>
                  <button
                    className={`${styles.userActionBtn} ${styles.userActionBtnDanger}`}
                    title={t('admin.delete')}
                    onClick={() => handleDeleteUser(user)}
                    disabled={actionLoading === 'del_user_' + user.id}
                  >🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedUser && (
          <div className={styles.userEditPanel}>
            <h3>✏️ {t('admin.editUser', { username: selectedUser.username })}</h3>
            <div className={styles.userEditGrid}>
              <div className={styles.userEditGroup}>
                <label>{t('admin.changeRole')}</label>
                <div className={styles.userEditRow}>
                  <select
                    className={styles.roleSelect}
                    value={newRole}
                    onChange={e => setNewRole(e.target.value)}
                  >
                    {Object.entries(adminApi.ROLE_LABELS).map(([val, label]) => (
                      <option key={val} value={val}>{label} ({val})</option>
                    ))}
                  </select>
                  <button
                    className={styles.actionBtn}
                    onClick={handleRoleChange}
                    disabled={actionLoading === 'role_' + selectedUser.id}
                  >
                    {actionLoading === 'role_' + selectedUser.id ? <span className={styles.btnSpinner}></span> : t('admin.save')}
                  </button>
                </div>
              </div>
              <div className={styles.userEditGroup}>
                <label>{t('admin.setNewPassword')}</label>
                <div className={styles.userEditRow}>
                  <input
                    type="password"
                    className={`${styles.userSearchInput} ${pwEditError ? styles.inputError : ''}`}
                    placeholder={t('admin.newPasswordPlaceholder')}
                    value={newPw}
                    onChange={e => { setNewPw(e.target.value); setPwEditError(null); }}
                  />
                  <button
                    className={styles.actionBtn}
                    onClick={handleResetPw}
                    disabled={!newPw || actionLoading === 'pw_' + selectedUser.id}
                  >
                    {actionLoading === 'pw_' + selectedUser.id ? <span className={styles.btnSpinner}></span> : t('admin.set')}
                  </button>
                </div>
                {pwEditError && (
                  <span className={styles.pwError}>{pwEditError}</span>
                )}
                <span className={styles.pwHint}>
                  {t('admin.pwHint')}
                </span>
              </div>
            </div>

            {/* Pro-User Rate-Limit-Overrides */}
            <div className={styles.rateLimitsBox}>
              <h4>⏱️ {t('admin.rateLimitOverrides')}</h4>
              {rateLimitsLoading ? (
                <span className={styles.pwHint}>{t('admin.loadingShort')}</span>
              ) : userRateLimits.length === 0 ? (
                <span className={styles.pwHint}>{t('admin.noOverridesSet')}</span>
              ) : (
                <div className={styles.rateLimitsList}>
                  {userRateLimits.map(rl => (
                    <div key={rl.limit_key} className={styles.rateLimitRow}>
                      <span>{rl.limit_key}</span>
                      <span>{t('admin.attemptsPerWindow', { max: rl.max_attempts, seconds: rl.window_seconds })}</span>
                      <button
                        className={`${styles.userActionBtn} ${styles.userActionBtnDanger}`}
                        title={t('admin.removeOverride')}
                        onClick={() => handleDeleteRateLimit(rl.limit_key)}
                        disabled={actionLoading === 'del_ratelimit_' + rl.limit_key}
                      >🗑️</button>
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.userEditRow} style={{ marginTop: '0.75rem' }}>
                <input
                  className={styles.userSearchInput}
                  style={{ maxWidth: 140 }}
                  value={newRateLimit.limit_key}
                  onChange={e => setNewRateLimit(p => ({ ...p, limit_key: e.target.value }))}
                  placeholder="limit-key"
                />
                <input
                  className={styles.userSearchInput}
                  style={{ maxWidth: 120 }}
                  type="number"
                  value={newRateLimit.max_attempts}
                  onChange={e => setNewRateLimit(p => ({ ...p, max_attempts: e.target.value }))}
                  placeholder={t('admin.maxAttempts')}
                />
                <input
                  className={styles.userSearchInput}
                  style={{ maxWidth: 140 }}
                  type="number"
                  value={newRateLimit.window_seconds}
                  onChange={e => setNewRateLimit(p => ({ ...p, window_seconds: e.target.value }))}
                  placeholder={t('admin.windowSeconds')}
                />
                <button className={styles.actionBtn} onClick={handleSetRateLimit} disabled={actionLoading === 'set_ratelimit'}>
                  {actionLoading === 'set_ratelimit' ? <span className={styles.btnSpinner}></span> : t('admin.set')}
                </button>
              </div>
            </div>

            <button className={styles.closeEditBtn} onClick={() => { setSelectedUser(null); setPwEditError(null); setNewPw(''); setNewRole(''); setUserRateLimits([]); }}>× {t('admin.close')}</button>
          </div>
        )}
      </section>
      )}

      {/* Band löschen */}
      {activeTab === 'catalog' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>🗑️</div>
          <div><h2>{t('admin.deleteBandGlobalTitle')}</h2><p>{t('admin.deleteBandGlobalDesc')}</p></div>
        </div>
        <div className={styles.deleteBandRow}>
          <input
            type="number"
            className={styles.deleteBandInput}
            placeholder={t('admin.bandIdPlaceholder')}
            value={deleteBandId}
            onChange={e => setDeleteBandId(e.target.value)}
            min="1"
          />
          <button
            className={styles.deleteBandBtn}
            onClick={handleDeleteBand}
            disabled={actionLoading === 'delete_band' || !deleteBandId}
          >
            {actionLoading === 'delete_band' ? <span className={styles.btnSpinner}></span> : `🗑️ ${t('admin.delete')}`}
          </button>
        </div>
      </section>
      )}

      {/* Shop-/Affiliate-Verwaltung */}
      {activeTab === 'shop' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>🛒</div>
          <div>
            <h2>{t('admin.shopMgmtTitle')}</h2>
            <p>{t('admin.shopMgmtDesc')}</p>
          </div>
        </div>

        <div className={styles.userSearchRow}>
          <button className={styles.actionBtn} onClick={loadShopProviders} disabled={shopProvidersLoading} style={{ maxWidth: 220 }}>
            {shopProvidersLoading ? <span className={styles.btnSpinner}></span> : `🔄 ${t('admin.loadProviders')}`}
          </button>
          <button className={styles.actionBtn} onClick={openNewProviderForm} style={{ maxWidth: 220 }}>
            ＋ {t('admin.newProvider')}
          </button>
        </div>

        {shopProviders.length > 0 && (
          <div className={styles.userTable}>
            <div className={styles.providerTableHeader}>
              <span>ID</span><span>{t('admin.name')}</span><span>{t('admin.urlTemplate')}</span><span>{t('admin.active')}</span><span>{t('admin.actions')}</span>
            </div>
            {shopProviders.map(provider => (
              <div key={provider.id} className={styles.providerRow}>
                <span className={styles.userId}>#{provider.id}</span>
                <span className={styles.userName2}>{provider.name}</span>
                <span className={styles.userEmail}>{provider.url_template}</span>
                <span className={provider.active ? styles.providerActive : styles.providerInactive}>
                  {provider.active ? t('admin.yes') : t('admin.no')}
                </span>
                <div className={styles.userActions}>
                  <button className={styles.userActionBtn} title={t('admin.edit')} onClick={() => openEditProviderForm(provider)}>✏️</button>
                  <button
                    className={`${styles.userActionBtn} ${styles.userActionBtnDanger}`}
                    title={t('admin.delete')}
                    onClick={() => handleDeleteProvider(provider)}
                    disabled={actionLoading === 'del_provider_' + provider.id}
                  >🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {providerFormOpen && (
          <div className={styles.userEditPanel}>
            <h3>{editingProvider ? `✏️ ${t('admin.editProvider', { name: editingProvider.name })}` : `➕ ${t('admin.newShopProvider')}`}</h3>
            <div className={styles.userEditGrid}>
              <div className={styles.userEditGroup}>
                <label>{t('admin.name')} *</label>
                <input className={styles.userSearchInput} value={providerForm.name} onChange={e => setProviderForm(p => ({ ...p, name: e.target.value }))} placeholder={t('admin.nameExample')} />
              </div>
              <div className={styles.userEditGroup}>
                <label>{t('admin.urlTemplate')} *</label>
                <input className={styles.userSearchInput} value={providerForm.url_template} onChange={e => setProviderForm(p => ({ ...p, url_template: e.target.value }))} placeholder="https://.../s?k={query}" />
              </div>
              <div className={styles.userEditGroup}>
                <label>{t('admin.affiliateParamKey')}</label>
                <input className={styles.userSearchInput} value={providerForm.affiliate_param_key} onChange={e => setProviderForm(p => ({ ...p, affiliate_param_key: e.target.value }))} placeholder="tag" />
              </div>
              <div className={styles.userEditGroup}>
                <label>{t('admin.affiliateParamValue')}</label>
                <input className={styles.userSearchInput} value={providerForm.affiliate_param_value} onChange={e => setProviderForm(p => ({ ...p, affiliate_param_value: e.target.value }))} placeholder="mangashelf-21" />
              </div>
              <div className={styles.userEditGroup}>
                <label>{t('admin.logoMediaKey')}</label>
                <input className={styles.userSearchInput} value={providerForm.logo_media_key} onChange={e => setProviderForm(p => ({ ...p, logo_media_key: e.target.value }))} placeholder="logo/shop/amazon.png" />
              </div>
              <div className={styles.userEditGroup}>
                <label>{t('admin.sortOrder')}</label>
                <input type="number" className={styles.userSearchInput} value={providerForm.sort_order} onChange={e => setProviderForm(p => ({ ...p, sort_order: e.target.value }))} />
              </div>
              <div className={styles.userEditGroup}>
                <label className={styles.notifToggleLabel}>
                  <input type="checkbox" checked={providerForm.active} onChange={e => setProviderForm(p => ({ ...p, active: e.target.checked }))} />
                  {' '}{t('admin.active')}
                </label>
              </div>
            </div>
            <div className={styles.userEditRow}>
              <button className={styles.actionBtn} onClick={handleSaveProvider} disabled={actionLoading === 'save_provider'}>
                {actionLoading === 'save_provider' ? <span className={styles.btnSpinner}></span> : t('admin.save')}
              </button>
              <button className={styles.closeEditBtn} onClick={() => { setProviderFormOpen(false); setEditingProvider(null); }}>{t('admin.cancel')}</button>
            </div>
          </div>
        )}
      </section>
      )}

      {/* Job-Runs Monitoring */}
      {activeTab === 'monitoring' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>⏱️</div>
          <div>
            <h2>{t('admin.jobRunsTitle')}</h2>
            <p>{t('admin.jobRunsDesc')}</p>
          </div>
        </div>

        <div className={styles.userSearchRow}>
          <input
            className={styles.userSearchInput}
            type="text"
            placeholder={t('admin.jobNameFilterPlaceholder')}
            value={jobNameFilter}
            onChange={e => setJobNameFilter(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadJobRuns()}
          />
          <button className={styles.actionBtn} onClick={loadJobRuns} disabled={jobRunsLoading}>
            {jobRunsLoading ? <span className={styles.btnSpinner}></span> : `🔄 ${t('admin.load')}`}
          </button>
        </div>

        {jobRuns.length === 0 ? (
          <div className={styles.noLogs}>
            <span>📭</span>
            <p>{t('admin.noJobRunsYet')}</p>
          </div>
        ) : (
          <div className={styles.userTable}>
            <div className={styles.jobRunTableHeader}>
              <span>{t('admin.job')}</span><span>{t('admin.started')}</span><span>{t('admin.finished')}</span><span>{t('admin.status')}</span><span>{t('admin.summary')}</span>
            </div>
            {jobRuns.map(run => (
              <div key={run.id} className={styles.jobRunRow}>
                <span className={styles.userName2}>{run.job_name}</span>
                <span className={styles.userEmail}>{run.started_at}</span>
                <span className={styles.userEmail}>{run.finished_at || '–'}</span>
                <span className={run.success ? styles.providerActive : styles.providerInactive}>
                  {run.success ? `✅ ${t('admin.ok')}` : `❌ ${t('admin.error')}`}
                </span>
                <span className={styles.userEmail}>{run.summary}</span>
              </div>
            ))}
          </div>
        )}
      </section>
      )}

      {/* Legacy-User-Audit */}
      {activeTab === 'users' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>🕵️</div>
          <div>
            <h2>{t('admin.legacyAuditTitle')}</h2>
            <p>{t('admin.legacyAuditDesc')}</p>
          </div>
        </div>

        <div className={styles.userSearchRow}>
          <button className={styles.actionBtn} onClick={() => runLegacyAudit(true)} disabled={legacyAuditLoading} style={{ maxWidth: 260 }}>
            {legacyAuditLoading ? <span className={styles.btnSpinner}></span> : `🔍 ${t('admin.runAuditDryRun')}`}
          </button>
          <button className={styles.deleteBandBtn} onClick={() => runLegacyAudit(false)} disabled={legacyAuditLoading} style={{ maxWidth: 260 }}>
            {legacyAuditLoading ? <span className={styles.btnSpinner}></span> : `⚠️ ${t('admin.cleanupInvalidUsers')}`}
          </button>
        </div>

        {legacyAuditResult && (
          <pre className={styles.auditResult}>{JSON.stringify(legacyAuditResult, null, 2)}</pre>
        )}
      </section>
      )}

      {/* Audit-Log (§59) */}
      {activeTab === 'monitoring' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>🕵️‍♂️</div>
          <div>
            <h2>{t('admin.auditLogTitle')}</h2>
            <p>{t('admin.auditLogDesc')}</p>
          </div>
        </div>

        <div className={styles.userSearchRow}>
          <button className={styles.actionBtn} onClick={() => loadAuditLog(null)} disabled={auditLogLoading} style={{ maxWidth: 220 }}>
            {auditLogLoading && !auditLogCursor ? <span className={styles.btnSpinner}></span> : `🔄 ${t('admin.loadLog')}`}
          </button>
        </div>

        {auditLog.length === 0 ? (
          <div className={styles.noLogs}>
            <span>📭</span>
            <p>{auditLogLoadedOnce ? t('admin.noAuditEntries') : t('admin.clickLoadLog')}</p>
          </div>
        ) : (
          <>
            <div className={styles.userTable}>
              <div className={styles.auditLogTableHeader}>
                <span>{t('admin.time')}</span><span>Admin</span><span>{t('admin.action')}</span><span>{t('admin.target')}</span><span>{t('admin.result')}</span><span>{t('admin.details')}</span>
              </div>
              {auditLog.map(entry => (
                <div key={entry.id} className={styles.auditLogRow}>
                  <span className={styles.userEmail}>{entry.created_at}</span>
                  <span className={styles.userEmail}>#{entry.admin_user_id}</span>
                  <span className={styles.userName2}>{entry.action}</span>
                  <span className={styles.userEmail}>{entry.target_type} #{entry.target_id}</span>
                  <span className={entry.result === 'SUCCESS' ? styles.providerActive : styles.providerInactive}>{entry.result}</span>
                  <span className={styles.userEmail}>{entry.details}</span>
                </div>
              ))}
            </div>
            {auditLogCursor != null && (
              <button className={styles.actionBtn} onClick={() => loadAuditLog(auditLogCursor)} disabled={auditLogLoading} style={{ maxWidth: 220 }}>
                {auditLogLoading ? <span className={styles.btnSpinner}></span> : t('admin.loadMore')}
              </button>
            )}
          </>
        )}
      </section>
      )}

      {/* Katalogverwaltung: Genres/Autoren/Verlage (§36) */}
      {activeTab === 'catalog' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>🏷️</div>
          <div>
            <h2>{t('admin.catalogMgmtTitle')}</h2>
            <p>{t('admin.catalogMgmtDesc')}</p>
          </div>
        </div>

        <div className={styles.catalogTabs}>
          {Object.entries(CATALOG_LABEL_KEYS).map(([res, labelKey]) => (
            <button
              key={res}
              className={`${styles.catalogTab} ${catalogResource === res ? styles.catalogTabActive : ''}`}
              onClick={() => { setCatalogResource(res); setCatalogItems([]); loadCatalog(res); }}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>

        <div className={styles.userSearchRow}>
          <input
            className={styles.userSearchInput}
            value={newCatalogName}
            onChange={e => setNewCatalogName(e.target.value)}
            placeholder={t('admin.newEntryIn', { resource: t(CATALOG_LABEL_KEYS[catalogResource]) })}
            onKeyDown={e => e.key === 'Enter' && handleCreateCatalogEntry()}
          />
          <button className={styles.actionBtn} onClick={handleCreateCatalogEntry} disabled={actionLoading === 'new_catalog' || !newCatalogName.trim()} style={{ maxWidth: 160 }}>
            {actionLoading === 'new_catalog' ? <span className={styles.btnSpinner}></span> : `＋ ${t('admin.create')}`}
          </button>
          <button className={styles.actionBtn} onClick={() => loadCatalog()} disabled={catalogLoading} style={{ maxWidth: 160 }}>
            {catalogLoading ? <span className={styles.btnSpinner}></span> : `🔄 ${t('admin.load')}`}
          </button>
        </div>

        {catalogItems.length > 0 && (
          <div className={styles.userTable}>
            <div className={styles.catalogTableHeader}>
              <span>ID</span><span>{t('admin.name')}</span><span>{t('admin.mergeIntoId')}</span><span>{t('admin.actions')}</span>
            </div>
            {catalogItems.map(item => (
              <div key={item.id} className={styles.catalogRow}>
                <span className={styles.userId}>#{item.id}</span>
                {catalogEditId === item.id ? (
                  <input
                    className={styles.userSearchInput}
                    value={catalogEditName}
                    onChange={e => setCatalogEditName(e.target.value)}
                    autoFocus
                  />
                ) : (
                  <span className={styles.userName2}>{item.name}</span>
                )}
                <input
                  className={styles.userSearchInput}
                  type="number"
                  placeholder={t('admin.targetId')}
                  value={catalogMergeTarget[item.id] || ''}
                  onChange={e => setCatalogMergeTarget(prev => ({ ...prev, [item.id]: e.target.value }))}
                />
                <div className={styles.userActions}>
                  {catalogEditId === item.id ? (
                    <>
                      <button className={styles.userActionBtn} title={t('admin.save')} onClick={() => handleRenameCatalogEntry(item.id)} disabled={actionLoading === 'rename_catalog_' + item.id}>💾</button>
                      <button className={styles.userActionBtn} title={t('admin.cancel')} onClick={() => setCatalogEditId(null)}>✕</button>
                    </>
                  ) : (
                    <button className={styles.userActionBtn} title={t('admin.rename')} onClick={() => { setCatalogEditId(item.id); setCatalogEditName(item.name); }}>✏️</button>
                  )}
                  <button className={styles.userActionBtn} title={t('admin.merge')} onClick={() => handleMergeCatalogEntry(item.id)} disabled={actionLoading === 'merge_catalog_' + item.id}>🔀</button>
                  <button className={`${styles.userActionBtn} ${styles.userActionBtnDanger}`} title={t('admin.delete')} onClick={() => handleDeleteCatalogEntry(item.id)} disabled={actionLoading === 'del_catalog_' + item.id}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      )}

      {/* API-Nutzungsstatistik */}
      {activeTab === 'monitoring' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>📡</div>
          <div>
            <h2>{t('admin.apiUsageTitle')}</h2>
            <p>{t('admin.apiUsageDesc')}</p>
          </div>
        </div>

        <div className={styles.userSearchRow}>
          <input
            className={styles.userSearchInput}
            type="number"
            placeholder={t('admin.userIdFilterPlaceholder')}
            value={apiUsageUserFilter}
            onChange={e => setApiUsageUserFilter(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadApiUsage()}
          />
          <button className={styles.actionBtn} onClick={loadApiUsage} disabled={apiUsageLoading} style={{ maxWidth: 160 }}>
            {apiUsageLoading ? <span className={styles.btnSpinner}></span> : `🔄 ${t('admin.load')}`}
          </button>
        </div>

        {apiUsage.length === 0 ? (
          <div className={styles.noLogs}>
            <span>📭</span>
            <p>{t('admin.noApiUsageData')}</p>
          </div>
        ) : (
          <>
            <div className={styles.apiUsageSummary}>
              <div className={styles.apiUsageSummaryCard}>
                <span className={styles.apiUsageSummaryValue}>{apiUsage.length}</span>
                <span className={styles.apiUsageSummaryLabel}>{t('admin.entries')}</span>
              </div>
              <div className={styles.apiUsageSummaryCard}>
                <span className={styles.apiUsageSummaryValue}>
                  {apiUsage.reduce((sum, r) => sum + (r.request_count || 0), 0)}
                </span>
                <span className={styles.apiUsageSummaryLabel}>{t('admin.totalCalls')}</span>
              </div>
              <div className={styles.apiUsageSummaryCard}>
                <span className={styles.apiUsageSummaryValue}>{apiUsageMedian}</span>
                <span className={styles.apiUsageSummaryLabel}>{t('admin.medianCallsPerEntry')}</span>
              </div>
              <div className={styles.apiUsageSummaryCard}>
                <span className={styles.apiUsageSummaryValue}>{apiUsageByUser.length}</span>
                <span className={styles.apiUsageSummaryLabel}>{t('admin.activeUsers')}</span>
              </div>
              <div className={styles.apiUsageSummaryCard} title={t('admin.avgCallsPerDayHint')}>
                <span className={styles.apiUsageSummaryValue}>{apiUsagePeriodAvg.toFixed(1)}</span>
                <span className={styles.apiUsageSummaryLabel}>{t('admin.avgCallsPerDay')}</span>
              </div>
            </div>

            {apiUsageOutliers.length > 0 && (
              <div className={styles.apiUsageOutliers}>
                <h3 className={styles.apiUsageChartTitle}>⚠️ {t('admin.suspiciousUsersTitle')}</h3>
                <p className={styles.apiUsageOutliersHint}>{t('admin.suspiciousUsersDesc')}</p>
                <div className={styles.userTable}>
                  <div className={styles.apiUsageOutlierTableHeader}>
                    <span>{t('admin.user')}</span><span>{t('admin.totalCalls')}</span><span>{t('admin.callsPerDay')}</span><span>{t('admin.activeDays')}</span><span></span>
                  </div>
                  {apiUsageOutliers.map((u) => (
                    <div key={u.userId} className={styles.apiUsageOutlierRow}>
                      <span className={styles.userId}>#{u.userId}</span>
                      <span className={styles.userName2}>{u.totalCalls}</span>
                      <span className={styles.userName2}>{u.callsPerDay.toFixed(1)}</span>
                      <span className={styles.userEmail}>{u.activeDays.toFixed(1)}</span>
                      <button
                        className={styles.apiUsageOutlierFilterBtn}
                        onClick={() => { setApiUsageUserFilter(String(u.userId)); loadApiUsage(); }}
                      >
                        🔍 {t('admin.filter')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.apiUsageChart}>
              <h3 className={styles.apiUsageChartTitle}>{t('admin.topEndpointsTitle')}</h3>
              {apiUsageChartData.entries.map((entry) => (
                <div key={entry.endpoint} className={styles.apiUsageBarRow}>
                  <span className={styles.apiUsageBarLabel} title={entry.endpoint}>{entry.endpoint}</span>
                  <div className={styles.apiUsageBarTrack}>
                    <div
                      className={styles.apiUsageBarFill}
                      style={{ width: `${apiUsageChartData.max > 0 ? (entry.count / apiUsageChartData.max) * 100 : 0}%` }}
                    />
                  </div>
                  <span className={styles.apiUsageBarValue}>{entry.count}</span>
                </div>
              ))}
            </div>

            <div className={styles.userTable}>
              <div className={styles.apiUsageTableHeader}>
                <span>{t('admin.user')}</span><span>{t('admin.method')}</span><span>{t('admin.endpoint')}</span><span>{t('admin.calls')}</span><span>{t('admin.lastCall')}</span>
              </div>
              {apiUsage.map((row, idx) => (
                <div key={idx} className={styles.apiUsageRow}>
                  <span className={styles.userId}>#{row.user_id}</span>
                  <span className={styles.userName2}>{row.method}</span>
                  <span className={styles.userEmail}>{row.endpoint}</span>
                  <span className={styles.userName2}>{row.request_count}</span>
                  <span className={styles.userEmail}>{row.last_request_at}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
      )}

      {/* Manga-/Band-Verwaltung (§48/§49) */}
      {activeTab === 'catalog' && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>📘</div>
          <div>
            <h2>{t('admin.mangaBandMgmtTitle')}</h2>
            <p>{t('admin.mangaBandMgmtDesc')}</p>
          </div>
        </div>

        {/* Neue Serie anlegen */}
        <div className={styles.userEditPanel}>
          <h3>➕ {t('admin.createNewSeries')}</h3>
          <div className={styles.userEditGrid}>
            <div className={styles.userEditGroup}>
              <label>{t('admin.titleLabel')} *</label>
              <input className={styles.userSearchInput} value={newMangaForm.title} onChange={e => setNewMangaForm(p => ({ ...p, title: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.publisher')}</label>
              <input className={styles.userSearchInput} value={newMangaForm.publisher} onChange={e => setNewMangaForm(p => ({ ...p, publisher: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.status')}</label>
              <select className={styles.roleSelect} value={newMangaForm.status} onChange={e => setNewMangaForm(p => ({ ...p, status: e.target.value }))}>
                <option value="RUNNING">RUNNING</option>
                <option value="COMPLETED">COMPLETED</option>
                <option value="HIATUS">HIATUS</option>
                <option value="CANCELLED">CANCELLED</option>
              </select>
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.genresCommaSeparated')}</label>
              <input className={styles.userSearchInput} value={newMangaForm.genres} onChange={e => setNewMangaForm(p => ({ ...p, genres: e.target.value }))} placeholder="Action, Drama" />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.coverMediaKey')}</label>
              <input className={styles.userSearchInput} value={newMangaForm.cover_media_key} onChange={e => setNewMangaForm(p => ({ ...p, cover_media_key: e.target.value }))} placeholder="cover/manga/1.png" />
            </div>
            <div className={styles.userEditGroup} style={{ gridColumn: '1 / -1' }}>
              <label>{t('admin.description')}</label>
              <textarea className={styles.userSearchInput} style={{ minHeight: 70 }} value={newMangaForm.description} onChange={e => setNewMangaForm(p => ({ ...p, description: e.target.value }))} />
            </div>
          </div>
          <button className={styles.actionBtn} onClick={handleCreateManga} disabled={actionLoading === 'create_manga'} style={{ maxWidth: 200 }}>
            {actionLoading === 'create_manga' ? <span className={styles.btnSpinner}></span> : t('admin.createSeries')}
          </button>
        </div>

        {/* Serie bearbeiten / (de)aktivieren */}
        <div className={styles.userEditPanel}>
          <h3>✏️ {t('admin.editSeriesTitle')}</h3>
          <div className={styles.userEditRow}>
            <input className={styles.userSearchInput} style={{ maxWidth: 160 }} type="number" placeholder={t('admin.mangaIdPlaceholder')} value={editMangaId} onChange={e => { setEditMangaId(e.target.value); setEditMangaLoaded(false); }} />
            <button className={styles.actionBtn} onClick={handleLoadMangaForEdit} disabled={actionLoading === 'load_manga' || !editMangaId} style={{ maxWidth: 160 }}>
              {actionLoading === 'load_manga' ? <span className={styles.btnSpinner}></span> : t('admin.load')}
            </button>
            <button className={styles.actionBtn} onClick={() => handleSetMangaActive(true)} disabled={actionLoading === 'toggle_manga_active' || !editMangaId} style={{ maxWidth: 160 }}>
              {t('admin.activate')}
            </button>
            <button className={styles.deleteBandBtn} onClick={() => handleSetMangaActive(false)} disabled={actionLoading === 'toggle_manga_active' || !editMangaId} style={{ maxWidth: 160 }}>
              {t('admin.deactivate')}
            </button>
          </div>

          {editMangaLoaded && (
            <>
              <div className={styles.userEditGrid} style={{ marginTop: '1rem' }}>
                <div className={styles.userEditGroup}>
                  <label>{t('admin.titleLabel')}</label>
                  <input className={styles.userSearchInput} value={editMangaForm.title} onChange={e => setEditMangaForm(p => ({ ...p, title: e.target.value }))} />
                </div>
                <div className={styles.userEditGroup}>
                  <label>{t('admin.publisher')}</label>
                  <input className={styles.userSearchInput} value={editMangaForm.publisher} onChange={e => setEditMangaForm(p => ({ ...p, publisher: e.target.value }))} />
                </div>
                <div className={styles.userEditGroup}>
                  <label>{t('admin.status')}</label>
                  <select className={styles.roleSelect} value={editMangaForm.status} onChange={e => setEditMangaForm(p => ({ ...p, status: e.target.value }))}>
                    <option value="RUNNING">RUNNING</option>
                    <option value="COMPLETED">COMPLETED</option>
                    <option value="HIATUS">HIATUS</option>
                    <option value="CANCELLED">CANCELLED</option>
                  </select>
                </div>
                <div className={styles.userEditGroup}>
                  <label>{t('admin.genresCommaSeparated')}</label>
                  <input className={styles.userSearchInput} value={editMangaForm.genres} onChange={e => setEditMangaForm(p => ({ ...p, genres: e.target.value }))} />
                </div>
                <div className={styles.userEditGroup}>
                  <label>{t('admin.coverMediaKey')}</label>
                  <input className={styles.userSearchInput} value={editMangaForm.cover_media_key} onChange={e => setEditMangaForm(p => ({ ...p, cover_media_key: e.target.value }))} />
                </div>
                <div className={styles.userEditGroup} style={{ gridColumn: '1 / -1' }}>
                  <label>{t('admin.description')}</label>
                  <textarea className={styles.userSearchInput} style={{ minHeight: 70 }} value={editMangaForm.description} onChange={e => setEditMangaForm(p => ({ ...p, description: e.target.value }))} />
                </div>
              </div>
              <button className={styles.actionBtn} onClick={handleUpdateManga} disabled={actionLoading === 'update_manga'} style={{ maxWidth: 200 }}>
                {actionLoading === 'update_manga' ? <span className={styles.btnSpinner}></span> : t('admin.saveChanges')}
              </button>
            </>
          )}
        </div>

        {/* Band zu Serie hinzufügen */}
        <div className={styles.userEditPanel}>
          <h3>➕ {t('admin.addBandToSeries')}</h3>
          <div className={styles.userEditGrid}>
            <div className={styles.userEditGroup}>
              <label>{t('admin.mangaId')} *</label>
              <input className={styles.userSearchInput} type="number" value={newBandMangaId} onChange={e => setNewBandMangaId(e.target.value)} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.bandNumber')} *</label>
              <input className={styles.userSearchInput} type="number" value={newBandForm.band_number} onChange={e => setNewBandForm(p => ({ ...p, band_number: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.bandTitle')}</label>
              <input className={styles.userSearchInput} value={newBandForm.band_title} onChange={e => setNewBandForm(p => ({ ...p, band_title: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.author')}</label>
              <input className={styles.userSearchInput} value={newBandForm.author} onChange={e => setNewBandForm(p => ({ ...p, author: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.publisher')}</label>
              <input className={styles.userSearchInput} value={newBandForm.publisher} onChange={e => setNewBandForm(p => ({ ...p, publisher: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.publishedDate')}</label>
              <input className={styles.userSearchInput} type="date" value={newBandForm.published_date} onChange={e => setNewBandForm(p => ({ ...p, published_date: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.coverMediaKey')}</label>
              <input className={styles.userSearchInput} value={newBandForm.cover_media_key} onChange={e => setNewBandForm(p => ({ ...p, cover_media_key: e.target.value }))} />
            </div>
          </div>
          <button className={styles.actionBtn} onClick={handleCreateBand} disabled={actionLoading === 'create_band'} style={{ maxWidth: 200 }}>
            {actionLoading === 'create_band' ? <span className={styles.btnSpinner}></span> : t('admin.createBand')}
          </button>
        </div>

        {/* Band bearbeiten / (de)aktivieren */}
        <div className={styles.userEditPanel}>
          <h3>✏️ {t('admin.editBandTitle')}</h3>
          <p className={styles.pwHint}>{t('admin.editBandHint')}</p>
          <div className={styles.userEditRow} style={{ marginBottom: '1rem' }}>
            <input className={styles.userSearchInput} style={{ maxWidth: 160 }} type="number" placeholder={t('admin.bandIdPlaceholder')} value={editBandId} onChange={e => setEditBandId(e.target.value)} />
            <button className={styles.actionBtn} onClick={() => handleSetBandActive(true)} disabled={actionLoading === 'toggle_band_active' || !editBandId} style={{ maxWidth: 160 }}>
              {t('admin.activate')}
            </button>
            <button className={styles.deleteBandBtn} onClick={() => handleSetBandActive(false)} disabled={actionLoading === 'toggle_band_active' || !editBandId} style={{ maxWidth: 160 }}>
              {t('admin.deactivate')}
            </button>
          </div>
          <div className={styles.userEditGrid}>
            <div className={styles.userEditGroup}>
              <label>{t('admin.bandNumber')} *</label>
              <input className={styles.userSearchInput} type="number" value={editBandForm.band_number} onChange={e => setEditBandForm(p => ({ ...p, band_number: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.bandTitle')}</label>
              <input className={styles.userSearchInput} value={editBandForm.band_title} onChange={e => setEditBandForm(p => ({ ...p, band_title: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.author')}</label>
              <input className={styles.userSearchInput} value={editBandForm.author} onChange={e => setEditBandForm(p => ({ ...p, author: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.publisher')}</label>
              <input className={styles.userSearchInput} value={editBandForm.publisher} onChange={e => setEditBandForm(p => ({ ...p, publisher: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.publishedDate')}</label>
              <input className={styles.userSearchInput} type="date" value={editBandForm.published_date} onChange={e => setEditBandForm(p => ({ ...p, published_date: e.target.value }))} />
            </div>
            <div className={styles.userEditGroup}>
              <label>{t('admin.coverMediaKey')}</label>
              <input className={styles.userSearchInput} value={editBandForm.cover_media_key} onChange={e => setEditBandForm(p => ({ ...p, cover_media_key: e.target.value }))} />
            </div>
          </div>
          <button className={styles.actionBtn} onClick={handleUpdateBand} disabled={actionLoading === 'update_band'} style={{ maxWidth: 200 }}>
            {actionLoading === 'update_band' ? <span className={styles.btnSpinner}></span> : t('admin.saveChanges')}
          </button>
        </div>
      </section>
      )}
      </div>
    </div>
  );
}

export default Admin;
